import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ChevronProps, DayPicker } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const VI_WEEKDAY_NAMES = ['CN', 'HAI', 'BA', 'TƯ', 'NĂM', 'SÁU', 'BẢY'];

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <>
      <style>{`
        .rdp-calendar .rdp-weekday:nth-child(1) { color: #dc2626; }
        .rdp-calendar .rdp-weekday:nth-child(7) { color: #16a34a; }
      `}</style>
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn('p-3 rdp-calendar', className)}
        classNames={{
          months: 'flex flex-col sm:flex-row space-y-2 sm:space-x-4 sm:space-y-0',
          month: 'space-y-2 relative',
          month_caption: 'flex justify-center pt-1 relative items-center',
          caption_label: 'text-sm font-medium',
          nav: 'flex items-center justify-between absolute top-1 left-1 right-1 h-7 z-10',
          button_previous: cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
          ),
          button_next: cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
          ),
          month_grid: 'w-full border-collapse space-y-1',
          weekdays: 'flex',
          weekday: 'rdp-weekday rounded-md w-9 font-bold text-[0.75rem] text-center text-gray-700',
          week: 'flex w-full mt-2',
          day: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          day_button: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-9 w-9 p-0 font-normal rounded-md aria-selected:opacity-100'
          ),
          range_end: 'day-range-end',
          selected:
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
          today: 'bg-accent text-accent-foreground',
          outside: 'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
          disabled: 'text-muted-foreground opacity-50',
          range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
          hidden: 'invisible',
          dropdowns: 'flex gap-1 items-center justify-center',
          dropdown_root: 'relative inline-flex items-center',
          dropdown:
            'h-7 px-1 py-0 border border-gray-300 rounded bg-white text-xs font-semibold focus:outline-none cursor-pointer outline-none appearance-none',
          ...classNames
        }}
        formatters={{
          formatWeekdayName: (date: Date) => VI_WEEKDAY_NAMES[date.getDay()] ?? ''
        }}
        components={{
          Chevron: ({ className, orientation, ...props }: ChevronProps) =>
            orientation === 'left' ? (
              <ChevronLeft className={cn('h-4 w-4', className)} {...props} />
            ) : (
              <ChevronRight className={cn('h-4 w-4', className)} {...props} />
            )
        }}
        {...props}
      />
    </>
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
